import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import EmailCaptureModal from '../components/EmailCaptureModal';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload,
  Loader2,
  Users,
  CheckCircle,
  Download,
  ArrowRight,
  Sparkles,
  FileSpreadsheet,
  UserCircle,
  PieChart,
  Mail,
  ChevronDown
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GenderDetectorPage = () => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [columns, setColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
    setSelectedColumn('');
    
    // Read columns from file to allow user to select the first name column
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API_URL}/api/tools/gender-detector/preview`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        }
      );
      
      if (response.data.columns && response.data.columns.length > 0) {
        setColumns(response.data.columns);
        // Auto-select column if we detect a good match
        const suggestedCol = response.data.suggested_column || '';
        setSelectedColumn(suggestedCol);
        setShowColumnSelector(true);
      }
    } catch (err) {
      console.log('Could not preview columns, will auto-detect');
      // Fallback - just process without column selection
      setShowColumnSelector(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const processFile = async () => {
    if (!file) return;
    
    setProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedColumn) {
        formData.append('name_column', selectedColumn);
      }
      
      const response = await axios.post(
        `${API_URL}/api/tools/gender-detector/process`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        }
      );
      
      setResult(response.data);
      setShowColumnSelector(false);
      
      if (response.data.total_rows > 100) {
        toast.info(`Detected gender for first 100 rows. ${response.data.total_rows - 100} rows remaining - sign up to process the rest.`);
      } else {
        toast.success('Gender detection completed!');
      }
      
    } catch (error) {
      console.error('Processing error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to process file';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const resetTool = () => {
    setFile(null);
    setResult(null);
  };

  const handleDownload = async () => {
    if (user?.email) {
      // Logged in users can download directly
      try {
        const downloadUrl = `${API_URL}${result.download_url}`;
        
        // Fetch the file as blob for better mobile compatibility
        const response = await fetch(downloadUrl);
        if (!response.ok) throw new Error('Download failed');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = result.output_filename || 'gender_detected.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
        
        toast.success('Download started!');
      } catch (error) {
        console.error('Download error:', error);
        toast.error('Download failed. Please try again.');
      }
    } else {
      // Show email capture modal for non-logged in users
      setShowEmailModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 sm:pb-12">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full text-pink-700 text-sm font-medium mb-4">
            <UserCircle className="w-4 h-4" />
            Free Gender Detector
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
            Gender Detector for CRM
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Detect gender from first names to personalize emails (Mr./Ms.) and understand your customer demographics.
          </p>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Mail className="w-6 h-6 text-pink-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Email Personalization</p>
            <p className="text-sm text-slate-500">Dear Mr./Ms. salutations</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <PieChart className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Demographics Insight</p>
            <p className="text-sm text-slate-500">Know your audience</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">First 100 Free</p>
            <p className="text-sm text-slate-500">No signup required</p>
          </div>
        </div>

        {!result ? (
          /* Upload Section */
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${file ? 'border-pink-400 bg-pink-50' : 'border-slate-300 hover:border-pink-400 hover:bg-slate-50'}`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              data-testid="gender-detector-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="gender-detector-file-input"
              />
              
              {file ? (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mx-auto">
                    <FileSpreadsheet className="w-7 h-7 text-pink-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); resetTool(); }}
                  >
                    Change file
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                    <Upload className="w-7 h-7 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Drop your CRM export here</p>
                    <p className="text-sm text-slate-500">CSV or Excel with first names (max 50MB)</p>
                  </div>
                </div>
              )}
            </div>

            {file && (
              <>
                {/* Column Selector - For better matching */}
                {showColumnSelector && columns.length > 0 && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-100">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <UserCircle className="w-4 h-4 text-pink-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Select the First Name column</p>
                        <p className="text-sm text-slate-600 mt-1">
                          For best results, tell us which column contains first names.
                        </p>
                      </div>
                    </div>
                    <Select value={selectedColumn} onValueChange={setSelectedColumn}>
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue placeholder="Select first name column..." />
                      </SelectTrigger>
                      <SelectContent>
                        {columns.map((col) => (
                          <SelectItem key={col} value={col}>
                            {col}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!selectedColumn && (
                      <p className="text-xs text-slate-500 mt-2">
                        We&apos;ll try to auto-detect if you skip this, but selecting ensures better accuracy.
                      </p>
                    )}
                  </div>
                )}
                
                <Button
                  onClick={processFile}
                  disabled={processing}
                  className="w-full mt-6 h-14 text-lg bg-pink-600 hover:bg-pink-700 text-white"
                  data-testid="gender-detector-process-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Detecting genders...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Detect Gender
                    </>
                  )}
                </Button>
              </>
            )}

            {/* Info */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-600">
                <strong>How it works:</strong> Upload a file with a first name column. 
                We&apos;ll detect gender and add Mr./Ms. salutations for email personalization.
              </p>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-900">{result.total_rows}</p>
                <p className="text-sm text-slate-500">Total Rows</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 text-center">
                <p className="text-2xl font-bold text-blue-600">{result.male_count}</p>
                <p className="text-sm text-blue-700">Male ({result.male_percentage}%)</p>
              </div>
              <div className="bg-pink-50 rounded-xl p-4 border border-pink-200 text-center">
                <p className="text-2xl font-bold text-pink-600">{result.female_count}</p>
                <p className="text-sm text-pink-700">Female ({result.female_percentage}%)</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
                <p className="text-2xl font-bold text-slate-600">{result.unknown_count}</p>
                <p className="text-sm text-slate-500">Unknown</p>
              </div>
            </div>

            {/* Gender Distribution Bar */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">Gender Distribution</h3>
              <div className="h-8 bg-slate-100 rounded-full overflow-hidden flex">
                <div 
                  className="bg-blue-500 h-full transition-all"
                  style={{ width: `${result.male_percentage}%` }}
                  title={`Male: ${result.male_percentage}%`}
                />
                <div 
                  className="bg-pink-500 h-full transition-all"
                  style={{ width: `${result.female_percentage}%` }}
                  title={`Female: ${result.female_percentage}%`}
                />
              </div>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-blue-600">Male: {result.male_percentage}%</span>
                <span className="text-pink-600">Female: {result.female_percentage}%</span>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Detection rate: {result.detection_rate}% of names successfully identified
              </p>
            </div>

            {/* Preview Table */}
            {result.preview && result.preview.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="font-semibold text-slate-900">Preview (First 5 rows)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Row</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Gender</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Salutation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.preview.map((row, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="px-4 py-3 text-sm text-slate-500">{row.row}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-900">{row.name || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                              row.gender === 'Male' ? 'bg-blue-100 text-blue-700' :
                              row.gender === 'Female' ? 'bg-pink-100 text-pink-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {row.gender || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{row.salutation || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Download Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Download Results</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {result.rows_over_limit > 0 
                      ? `First 100 rows processed. ${result.rows_over_limit} rows remaining.`
                      : `All ${result.total_rows} rows processed.`
                    }
                  </p>
                </div>
                <Button
                  onClick={handleDownload}
                  className="bg-pink-600 hover:bg-pink-700 text-white"
                  data-testid="gender-detector-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
              </div>
              
              {result.rows_over_limit > 0 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                  <p className="text-sm text-indigo-800 font-medium mb-2">
                    Need to process all {result.total_rows} rows?
                  </p>
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="text-indigo-600 border-indigo-200">
                      Sign Up Free
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Process Another */}
            <div className="text-center">
              <Button variant="outline" onClick={resetTool}>
                Process Another File
              </Button>
            </div>
          </div>
        )}

        {/* Use Cases */}
        <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4 text-center">
            Why Use Gender Detection?
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex gap-3">
              <Mail className="w-5 h-5 text-pink-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">Personalized Emails</p>
                <p className="text-sm text-slate-500">Address contacts as &quot;Dear Mr. Smith&quot; or &quot;Dear Ms. Johnson&quot; for a professional touch.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <PieChart className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">Customer Analytics</p>
                <p className="text-sm text-slate-500">Understand your audience demographics and tailor your marketing accordingly.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Users className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">Segmentation</p>
                <p className="text-sm text-slate-500">Create targeted campaigns for different audience segments.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-slate-900">Better Engagement</p>
                <p className="text-sm text-slate-500">Personalized communication leads to higher open and response rates.</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <SupportButton />

      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        tool="gender-detector"
        fileId={result?.download_url?.split('/').pop()}
        toolName="Gender Detector"
        onSuccess={() => {
          // Trigger download after email capture
          const downloadUrl = `${API_URL}${result.download_url}`;
          window.open(downloadUrl, '_blank');
          toast.success('Download started!');
          setShowEmailModal(false);
        }}
      />
    </div>
  );
};

export default GenderDetectorPage;
